import React, { useState, useEffect } from 'react'
import { X, Filter, Calendar, Tag, User, Briefcase } from 'lucide-react'
import { getColors } from '../../../theme/colors'
import { dateUtils } from '../../../api'

const FilterPanel = ({
  filters,
  onFilterChange,
  onClose,
  categories,
  employees,
  isAdmin,
  activeTab
}) => {
  const colors = getColors()
  const [localFilters, setLocalFilters] = useState(filters)

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const handleInputChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const applyFilters = () => {
    onFilterChange(localFilters)
    onClose()
  }

  const clearAllFilters = () => {
    const clearedFilters = {
      type: '',
      category: '',
      startDate: '',
      endDate: '',
      designation: '',
      userId: ''
    }
    setLocalFilters(clearedFilters)
    onFilterChange(clearedFilters)
  }

  const setDateRange = range => {
    const dateRange = dateUtils.getDateRange(range)
    setLocalFilters(prev => ({
      ...prev,
      startDate: dateRange.startDate || '',
      endDate: dateRange.endDate || ''
    }))
  }

  return (
    <div
      className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6 mb-6`}
    >
      {/* Header */}
      <div className='flex items-center justify-between mb-6'>
        <h3
          className={`${colors.textPrimary} text-lg font-semibold flex items-center gap-2`}
        >
          <Filter size={20} />
          Filter Options
        </h3>
        <button
          onClick={onClose}
          className={`${colors.textSecondary} hover:${colors.textPrimary} transition-colors`}
        >
          <X size={20} />
        </button>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
        {/* Entry Type Filter */}
        <div>
          <label
            className={`block ${colors.textPrimary} text-sm font-medium mb-2`}
          >
            Entry Type
          </label>
          <select
            value={localFilters.type}
            onChange={e => handleInputChange('type', e.target.value)}
            className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
          >
            <option value=''>All Types</option>
            <option value='Income'>Income</option>
            <option value='Expense'>Expense</option>
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <label
            className={`block ${colors.textPrimary} text-sm font-medium mb-2 flex items-center gap-2`}
          >
            <Tag size={16} />
            Category
          </label>
          <select
            value={localFilters.category}
            onChange={e => handleInputChange('category', e.target.value)}
            className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
          >
            <option value=''>All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        {/* User Filter (Admin in "All" tab) */}
        {isAdmin && activeTab === 'all' && (
          <div>
            <label
              className={`block ${colors.textPrimary} text-sm font-medium mb-2 flex items-center gap-2`}
            >
              <User size={16} />
              User
            </label>
            <select
              value={localFilters.userId}
              onChange={e => handleInputChange('userId', e.target.value)}
              className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
            >
              <option value=''>All Users</option>
              {employees.map(employee => (
                <option key={employee._id} value={employee._id}>
                  {employee.username} -{' '}
                  {employee.employeeData?.designation || employee.role}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Designation Filter (Admin in "All" tab) */}
        {isAdmin && activeTab === 'all' && (
          <div>
            <label
              className={`block ${colors.textPrimary} text-sm font-medium mb-2 flex items-center gap-2`}
            >
              <Briefcase size={16} />
              Designation
            </label>
            <select
              value={localFilters.designation}
              onChange={e => handleInputChange('designation', e.target.value)}
              className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
            >
              <option value=''>All Designations</option>
              {[
                ...new Set(
                  employees
                    .map(emp => emp.employeeData?.designation)
                    .filter(Boolean)
                )
              ].map(designation => (
                <option key={designation} value={designation}>
                  {designation}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Start Date */}
        <div>
          <label
            className={`block ${colors.textPrimary} text-sm font-medium mb-2 flex items-center gap-2`}
          >
            <Calendar size={16} />
            Start Date
          </label>
          <input
            type='date'
            value={localFilters.startDate}
            onChange={e => handleInputChange('startDate', e.target.value)}
            className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
          />
        </div>

        {/* End Date */}
        <div>
          <label
            className={`block ${colors.textPrimary} text-sm font-medium mb-2 flex items-center gap-2`}
          >
            <Calendar size={16} />
            End Date
          </label>
          <input
            type='date'
            value={localFilters.endDate}
            onChange={e => handleInputChange('endDate', e.target.value)}
            className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
          />
        </div>
      </div>

      {/* Quick Date Range Buttons */}
      <div className='mb-6'>
        <label
          className={`block ${colors.textPrimary} text-sm font-medium mb-2`}
        >
          Quick Date Ranges
        </label>
        <div className='flex flex-wrap gap-2'>
          {[
            { label: 'Today', value: 'today' },
            { label: 'Last 7 Days', value: 'week' },
            { label: 'Last 30 Days', value: 'month' },
            { label: 'Last Year', value: 'year' }
          ].map(range => (
            <button
              key={range.value}
              onClick={() => setDateRange(range.value)}
              className={`px-4 py-2 rounded-lg border ${colors.cardBorder} ${colors.textSecondary} hover:${colors.textPrimary} hover:${colors.cardBorderHover} transition-all duration-200`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className='flex gap-3 pt-4 border-t border-gray-700'>
        <button
          onClick={clearAllFilters}
          className={`flex-1 bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300`}
        >
          Clear All
        </button>
        <button
          onClick={applyFilters}
          className={`flex-1 bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary} py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300`}
        >
          Apply Filters
        </button>
      </div>
    </div>
  )
}

export default FilterPanel
